<template>
  <div ref="sceneContainer" class="scene-container"></div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, onUnmounted, watch } from 'vue';
import * as THREE from 'three';
import { useMainStore } from '@/stores/mainStore';
import { groupColorsByHue, colorFamilyOrder, mockColorData} from '@/utils/colorSorter';
import { markRaw } from 'vue'; // 【新增】导入 markRaw

// --- 采集模式（?capture=1）：确定性步进 + 透明底，仅用于导出序列帧 ---
// 未带该参数时，以下开关恒为 false，运行时行为与改造前完全一致。
const CAPTURE = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('capture');
let virtualTime = 0;

const sceneContainer = ref(null);
// 【改动】store 现在是所有参数的唯一来源
const store = useMainStore();

// --- THREE.JS Core Variables ---
let renderer, scene, raycaster;
// 【改动】相机管理
let orthographicCamera, perspectiveCamera, currentCamera; 
const mouse = new THREE.Vector2(9999, 9999);
const clock = new THREE.Clock();
let animationFrameId;

// --- 用于鼠标缓动的变量 ---
const smoothedMouse = new THREE.Vector2(9999, 9999);

// --- 交互与视觉分离的核心对象 ---
let colorPlanes;
let invisibleHitbox;
let hoveredPlane;
let currentHoveredId = -1;

// --- 用于平滑过渡的状态变量 ---
let isTransitioning = false;
let exitingPlanes = null;
let exitingHitbox = null;
let queuedPoints = null;

// --- 用于平滑颜色过渡的变量 ---
let hoveredPlaneTargetColor = new THREE.Color(0xffffff);
let hoveredPlaneCurrentColor = new THREE.Color(0xffffff);

// --- 用于控制状态混合的变量 ---
let masterIntensity = 0.0;

// --- 持续滚动效果 ---
let scrollOffset = 0;

// --- 【改动】所有硬编码的配置参数均已移除, 它们现在位于 Pinia store 中 ---

// --- 着色器 ---
// 【改动】为着色器添加 uniforms，以便接收来自JS的动态参数
const backgroundVertexShader = `
  attribute float a_visibility;
  varying vec2 vUv;
  varying vec3 vInstanceColor;
  varying float vVisibility;

  // 新增 uniforms
  uniform float u_slant;
  uniform float u_squash_factor;
  uniform float u_size_factor;

  void main() {
    vUv = uv;
    vInstanceColor = instanceColor;
    vVisibility = a_visibility;
    vec3 instancePos = instanceMatrix[3].xyz;
    vec4 modifiedPosition = vec4(position, 1.0);
    
    // 使用 uniform 控制梯形倾斜
    float slantAmount = -instancePos.x * u_slant;
    modifiedPosition.y += position.x * slantAmount * uv.y;
    
    float mountainFactor = (cos(instancePos.x / 20.0) + 0.5) / 2.5;
    
    // 使用 uniform 控制形变
    float squashFactor = 0.1 + mountainFactor * u_squash_factor;
    modifiedPosition.x *= (1.0 - vUv.y * squashFactor);
    
    // 使用 uniform 控制大小
    float sizeFactor = 0.8 + mountainFactor * u_size_factor;
    modifiedPosition.xy *= sizeFactor;
    
    gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * modifiedPosition;
  }
`;
const backgroundFragmentShader = `
  varying vec2 vUv;
  varying vec3 vInstanceColor;
  varying float vVisibility;

  // 【新增】接收动态参数的 Uniforms
  uniform float u_trail_factor;
  uniform float u_top_color_multiplier;
  uniform float u_bottom_color_multiplier;
  uniform float u_global_alpha;

  void main() {
    float trailFactor = pow(vUv.y, u_trail_factor);
    vec3 topColor = vInstanceColor * u_top_color_multiplier;
    vec3 bottomColor = vInstanceColor * u_bottom_color_multiplier;
    vec3 finalColor = mix(bottomColor, topColor, trailFactor);
    gl_FragColor = vec4(finalColor, trailFactor * vVisibility * u_global_alpha);
  }
`;
const hoverVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const hoverFragmentShader = `
  varying vec2 vUv;
  uniform vec3 uColor;
  void main() {
    float trailFactor = pow(vUv.y, 2.0);
    vec3 topColor = uColor * 1.5;
    vec3 bottomColor = uColor * 0.5;
    vec3 finalColor = mix(bottomColor, topColor, trailFactor);
    gl_FragColor = vec4(finalColor, 1.0);
  }
`;


// --- VUE 生命周期 ---
onMounted(() => {
  initScene();

  // 【新增】在初始化后将 canvas 元素注册到 store
  store.setCanvas(renderer.domElement); 



  // 【改动】监听参数变化，如果平面数量变了，需要重启场景
   watch(() => [
    store.sceneParams?.PLANE_COUNT_X, 
    store.sceneParams?.PLANE_COUNT_Y,
    store.sceneParams?.planeVisualHeight, // 新增
    store.sceneParams?.planeDepthSpacing // 新增
  ], () => {
    if (colorPlanes) restartScene();
  });

  // 【改动】监听相机变化
  watch(() => [
    store.sceneParams?.cameraType,
    store.sceneParams?.perspectiveFov,
    store.sceneParams?.perspectiveZ,
  ], updateCamera, { immediate: true });
  
  // 【改动】监听混合模式的变化，并直接更新材质
  watch(() => store.sceneParams.shader_blend_mode, (newMode) => {
      if (colorPlanes && colorPlanes.material) {
          colorPlanes.material.blending = newMode === 'Additive' ? THREE.AdditiveBlending : THREE.NormalBlending;
          colorPlanes.material.needsUpdate = true;
      }
      if(exitingPlanes && exitingPlanes.material){
          exitingPlanes.material.blending = newMode === 'Additive' ? THREE.AdditiveBlending : THREE.NormalBlending;
          exitingPlanes.material.needsUpdate = true;
      }
  });

  restartScene(); // 使用新函数进行初始启动

  if (CAPTURE) {
    // 采集模式：不启动 rAF 循环，全部由外部以固定 dt 驱动，保证逐帧确定性
    renderer.setClearColor(0x000000, 0); // 透明底
    window.__CAPTURE__ = {
      ready: true,
      store,
      step: (delta) => step(delta),
      dataURL: () => renderer.domElement.toDataURL('image/png'),
      size: () => ({ w: renderer.domElement.width, h: renderer.domElement.height }),
      // 用于无缝循环：直接把状态强度设到周期不动点，省去多轮预热
      setMasterIntensity: (v) => { masterIntensity = v; },
      getMasterIntensity: () => masterIntensity,
      // 背景：null=透明底；传十六进制数值=实心底（如 0xFFFFFF）
      setBackground: (color) => {
        scene.background = color === null ? null : new THREE.Color(color);
      },
      // 当前数据下实际参与布局的色系数（影响滚动的空间周期）
      familyCount: () => colorFamilyOrder.filter(f => groupColorsByHue(store.colorPoints)[f].length > 0).length,
    };
  } else {
    animate(); // 开启动画循环
  }

  window.addEventListener('resize', handleResize);
  if (renderer) {
    renderer.domElement.addEventListener('mousemove', onMouseMove, { passive: true });
    renderer.domElement.addEventListener('click', onClick);
    renderer.domElement.addEventListener('mouseleave', onMouseLeave);
  }
});

onUnmounted(() => {
  cancelAnimationFrame(animationFrameId);
  window.removeEventListener('resize', handleResize);
  if (renderer && renderer.domElement) {
    scene.traverse(object => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
            if (Array.isArray(object.material)) {
                object.material.forEach(material => material.dispose());
            } else {
                object.material.dispose();
            }
        }
    });
    renderer.domElement.removeEventListener('mousemove', onMouseMove);
    renderer.domElement.removeEventListener('click', onClick);
    renderer.domElement.removeEventListener('mouseleave', onMouseLeave);
    renderer.dispose();
  }
});

// --- Watch 监听器 ---
watch(() => store.colorPoints, (newPoints) => {
  // 使用 Array.isArray 进行严格检查，确保我们只处理数组
  if (!Array.isArray(newPoints) || !scene) {
    // 如果 store.colorPoints 变成 null 或不是数组，则不做任何事
    // 如果场景还没准备好，也不做任何事
    return;
  }
  
  // 即使 newPoints 是一个空数组（代表筛选结果为空），我们也应该启动过渡去清空场景
  
  if (isTransitioning) {
    queuedPoints = newPoints;
    return;
  }
  startTransition(newPoints);
}, { deep: true, immediate: true }); 


// --- 【新增】重启场景函数 ---
function restartScene() {
    // 清理旧对象
    // 【修正】createVisuals 在筛选结果为空时会返回没有 geometry/material 的 Object3D 占位符，
    // 直接 dispose() 会抛 "Cannot read properties of undefined"，此处与 startTransition 同样做空判断
    if (colorPlanes) {
      scene.remove(colorPlanes);
      if (colorPlanes.geometry && colorPlanes.geometry.dispose) colorPlanes.geometry.dispose();
      if (colorPlanes.material && colorPlanes.material.dispose) colorPlanes.material.dispose();
    }
    if (invisibleHitbox) {
      scene.remove(invisibleHitbox);
      if (invisibleHitbox.geometry && invisibleHitbox.geometry.dispose) invisibleHitbox.geometry.dispose();
      if (invisibleHitbox.material && invisibleHitbox.material.dispose) invisibleHitbox.material.dispose();
    }
    
    scrollOffset = 0; // 重置滚动位置

    // 创建新对象
    const initialVisuals = createVisuals(store.colorPoints);
    colorPlanes = initialVisuals.planes;
    invisibleHitbox = initialVisuals.hitbox;
    scene.add(colorPlanes, invisibleHitbox);
}

function startTransition(points) {
  isTransitioning = true;
  exitingPlanes = colorPlanes;
  exitingHitbox = invisibleHitbox;
  if (exitingHitbox) exitingHitbox.raycast = () => {};
  
  const newVisuals = createVisuals(points);
  colorPlanes = newVisuals.planes;
  invisibleHitbox = newVisuals.hitbox;
  scene.add(colorPlanes, invisibleHitbox);
  
  exitingPlanes.renderOrder = 0;
  colorPlanes.renderOrder = 1;

  setTimeout(() => {
    // 【修正】在调用 dispose 前，确保 exitingPlanes 是一个有效的 InstancedMesh，
    // 而不是一个没有 geometry 或 material 的空白占位符。
    if (exitingPlanes && exitingPlanes.geometry && exitingPlanes.material) {
      scene.remove(exitingPlanes);
      exitingPlanes.geometry.dispose();
      exitingPlanes.material.dispose();
    }
    // 对 hitbox 也进行同样的安全检查
    if (exitingHitbox && exitingHitbox.geometry && exitingHitbox.material) {
      scene.remove(exitingHitbox);
      exitingHitbox.geometry.dispose();
      exitingHitbox.material.dispose();
    }
    
    // 重置变量
    exitingPlanes = null;
    exitingHitbox = null;

    if (queuedPoints) {
      const nextPoints = queuedPoints;
      queuedPoints = null;
      startTransition(nextPoints);
    } else {
      isTransitioning = false;
    }
  }, store.sceneParams.TRANSITION_DURATION);
}


// --- THREE.JS 核心功能 ---
function initScene() {
  const container = sceneContainer.value;
  scene = new THREE.Scene();
  scene.background = CAPTURE ? null : new THREE.Color(0xFAFAFA);

  const aspect = container.clientWidth / container.clientHeight;
  const frustumSize = 50;
orthographicCamera = new THREE.OrthographicCamera(frustumSize * aspect / -2, frustumSize * aspect / 2, frustumSize / 2, frustumSize / -2, 1, 1000);
  perspectiveCamera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);

  // 默认相机位置
  orthographicCamera.position.set(0, 0, 100);
  perspectiveCamera.position.set(0, 0, 80);

  // camera = new THREE.OrthographicCamera(frustumSize * aspect / -2, frustumSize * aspect / 2, frustumSize / 2, frustumSize / -2, 1, 1000);
  // camera.position.set(0, 0, 100);

 renderer = new THREE.WebGLRenderer({
  antialias: true,
  alpha: true,
  preserveDrawingBuffer: true // 【新增】保留绘图缓冲区
});
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.setPixelRatio(window.devicePixelRatio);
  container.appendChild(renderer.domElement);

  raycaster = new THREE.Raycaster();

  // 【新增】在初始化时设置一次相机
  updateCamera();

  // 【核心修改】使用 markRaw 包裹实例
  store.setSceneRendererAndCamera(markRaw(scene), markRaw(renderer), markRaw(currentCamera));

// 【修改】在 watch 监听器中也使用 markRaw
watch(() => [
    store.sceneParams?.cameraType,
    store.sceneParams?.perspectiveFov,
    store.sceneParams?.perspectiveZ,
  ], () => {
    updateCamera();
    // 每次相机切换或参数改变时，更新 store 中的相机实例
    store.setSceneRendererAndCamera(markRaw(scene), markRaw(renderer), markRaw(currentCamera));
  }, { immediate: true });

  
  const hoverGeometry = new THREE.PlaneGeometry(1.5, 1.5);
  const hoverMaterial = new THREE.ShaderMaterial({
    vertexShader: hoverVertexShader,
    fragmentShader: hoverFragmentShader,
    transparent: true,
    depthWrite: false,
    uniforms: { uColor: { value: hoveredPlaneCurrentColor } }
  });
  hoveredPlane = new THREE.Mesh(hoverGeometry, hoverMaterial);
  hoveredPlane.visible = false;
  hoveredPlane.renderOrder = 999;
  scene.add(hoveredPlane);
}

// 【新增】更新相机的函数
function updateCamera() {
    if (!store.sceneParams) return;
    
    const type = store.sceneParams.cameraType;
    if (type === 'Perspective') {
        currentCamera = perspectiveCamera;
        currentCamera.fov = store.sceneParams.perspectiveFov;
        currentCamera.position.z = store.sceneParams.perspectiveZ;
    } else {
        currentCamera = orthographicCamera;
    }
    handleResize(); // 更新相机后需要立即调整尺寸
}

// 【修改这里】
function createVisuals(points) {
    // 检查传入的是否为有效数组，如果筛选结果为空，则创建一个空的占位符
    if (!Array.isArray(points) || points.length === 0) {
        const placeholder = new THREE.Object3D();
        placeholder.raycast = () => {}; // 确保空的 hitbox 不响应射线检测
        return { planes: placeholder, hitbox: placeholder };
    }
    
    // 1. 将传入的、已过滤的颜色点按色系分组
    const colorGroups = groupColorsByHue(points);

    // 2. 过滤掉本次筛选结果中没有颜色的色系
    const availableFamilies = colorFamilyOrder.filter(family => colorGroups[family].length > 0);
    
    // 如果过滤后，所有颜色组都为空，也返回占位符
    if (availableFamilies.length === 0) {
        const placeholder = new THREE.Object3D();
        placeholder.raycast = () => {};
        return { planes: placeholder, hitbox: placeholder };
    }

    // --- 几何体与材质的创建逻辑保持不变 (下面的代码您无需改动) ---
    const totalPlanes = store.sceneParams.PLANE_COUNT_X * store.sceneParams.PLANE_COUNT_Y;
    const backgroundGeometry = new THREE.PlaneGeometry(2, store.sceneParams.planeVisualHeight);
    const hitboxGeometry = new THREE.PlaneGeometry(2, 3);
    const tempObject = new THREE.Object3D();
    const tempColor = new THREE.Color();
    const { width, height } = getCameraFrustum();
    const visibility = new Float32Array(totalPlanes).fill(0.0);
    backgroundGeometry.setAttribute('a_visibility', new THREE.InstancedBufferAttribute(visibility, 1));
    
    const backgroundMaterial = new THREE.ShaderMaterial({
        vertexShader: backgroundVertexShader,
        fragmentShader: backgroundFragmentShader,
        transparent: true,
        depthWrite: false,
        blending: store.sceneParams.shader_blend_mode === 'Additive' ? THREE.AdditiveBlending : THREE.NormalBlending,
        uniforms: {
            u_trail_factor: { value: store.sceneParams.shader_trail_factor },
            u_top_color_multiplier: { value: store.sceneParams.shader_top_color_multiplier },
            u_bottom_color_multiplier: { value: store.sceneParams.shader_bottom_color_multiplier },
            u_global_alpha: { value: store.sceneParams.shader_global_alpha },
            u_slant: { value: store.sceneParams.shader_vertex_slant },
            u_squash_factor: { value: store.sceneParams.shader_vertex_squash },
            u_size_factor: { value: store.sceneParams.shader_vertex_size },
        }
    });
    
    const newPlanes = new THREE.InstancedMesh(backgroundGeometry, backgroundMaterial, totalPlanes);
    const hitboxMaterial = new THREE.MeshBasicMaterial({ visible: false, depthWrite: false });
    const newHitbox = new THREE.InstancedMesh(hitboxGeometry, hitboxMaterial, totalPlanes);
    
    // 【重要】为 hitbox 的 userData 创建一个空对象，用于存储映射关系
    newHitbox.userData = {};

    // --- 【核心渲染逻辑修改】 ---
    for (let i = 0; i < totalPlanes; i++) {
        const row = Math.floor(i / store.sceneParams.PLANE_COUNT_X);
        const col = i % store.sceneParams.PLANE_COUNT_X;

        // 3. 为每一行决定一个色系
        const familyName = availableFamilies[row % availableFamilies.length];
        const currentFamilyColors = colorGroups[familyName];
        
        // 4. 从该行的色系中循环选择一个颜色
        const point = currentFamilyColors[col % currentFamilyColors.length];
        
        // 5. 为色块设置颜色
        newPlanes.setColorAt(i, tempColor.set(point.color.hex));
        
        // 【关键】将该色块(instanceId)对应的完整 point 数据存起来
        newHitbox.userData[i] = { point: point };
        
        // --- 位置计算和矩阵设置逻辑保持不变 ---
        const xOffset = (row % 2) * (width / store.sceneParams.PLANE_COUNT_X / 2);
        const basePosition = new THREE.Vector3(
            (col / store.sceneParams.PLANE_COUNT_X) * width - width / 2 + xOffset,
            (row / store.sceneParams.PLANE_COUNT_Y) * height - height / 2,
            row * store.sceneParams.planeDepthSpacing 
        );
        tempObject.position.copy(basePosition);
        tempObject.scale.set(0.01, 0.01, 0.01); 
        tempObject.updateMatrix();
        newPlanes.setMatrixAt(i, tempObject.matrix);
        const hitboxPosition = basePosition.clone();
        hitboxPosition.y += 1.5;
        tempObject.scale.set(1, 1, 1); 
        tempObject.position.copy(hitboxPosition);
        tempObject.updateMatrix();
        newHitbox.setMatrixAt(i, tempObject.matrix);
    }

    newPlanes.instanceMatrix.needsUpdate = true;
    newPlanes.instanceColor.needsUpdate = true;
    newHitbox.instanceMatrix.needsUpdate = true;

    return { planes: newPlanes, hitbox: newHitbox };
}

// 【改造】把原 animate 拆分为 step(delta)：由外部给定固定 dt，保证逐帧可复现。
// animate 只是 rAF 薄封装，非采集模式行为不变。
function step(delta) {
  if (!store.sceneParams || !currentCamera) return; // 【改动】检查 currentCamera

  virtualTime += delta;
  const elapsedTime = virtualTime;

  // 【改动】在动画循环中，使用 store 中的所有参数
  scrollOffset -= store.sceneParams.SCROLL_SPEED * delta;
  smoothedMouse.lerp(mouse, store.sceneParams.MOUSE_SMOOTHING_FACTOR);

  const cycleTime = elapsedTime % store.sceneParams.STATE_CYCLE_DURATION;
  const isWaveState = cycleTime < store.sceneParams.WAVE_STATE_DURATION;
  const targetIntensity = isWaveState ? 1.0 : 0.0;
  const transitionSpeed = isWaveState ? store.sceneParams.FADE_IN_SPEED : store.sceneParams.FADE_OUT_SPEED;
  masterIntensity = THREE.MathUtils.lerp(masterIntensity, targetIntensity, transitionSpeed * delta);

  if (exitingPlanes && exitingPlanes.geometry) {
    animatePlanes(exitingPlanes, delta, elapsedTime, true);
  }

  if (colorPlanes && colorPlanes.geometry) {
    // 【改动】实时更新 shader uniforms
    const uniforms = colorPlanes.material.uniforms;
    uniforms.u_trail_factor.value = store.sceneParams.shader_trail_factor;

    uniforms.u_top_color_multiplier.value = store.sceneParams.shader_top_color_multiplier;
    uniforms.u_bottom_color_multiplier.value = store.sceneParams.shader_bottom_color_multiplier;
    uniforms.u_global_alpha.value = store.sceneParams.shader_global_alpha;

    // 【新增】更新顶点 uniforms
    uniforms.u_slant.value = store.sceneParams.shader_vertex_slant;
    uniforms.u_squash_factor.value = store.sceneParams.shader_vertex_squash;
    uniforms.u_size_factor.value = store.sceneParams.shader_vertex_size;
    
    animatePlanes(colorPlanes, delta, elapsedTime, false);
  }

  updateInteraction();
  updateHoveredPlane();

  renderer.render(scene, currentCamera);
}

function animate() {
  animationFrameId = requestAnimationFrame(animate);
  step(Math.min(clock.getDelta(), 1 / 30));
}

function animatePlanes(planes, delta, elapsedTime, isExiting) {
  const { width, height } = getCameraFrustum();
  const tempMatrix = new THREE.Matrix4();
  const currentPosition = new THREE.Vector3();
  const currentQuaternion = new THREE.Quaternion();
  const currentScale = new THREE.Vector3();
  const targetPosition = new THREE.Vector3();
  const targetScale = new THREE.Vector3();
  const visibilityAttribute = planes.geometry.attributes.a_visibility;

  // 【改动】所有参数来自 store
  const pCountX = store.sceneParams.PLANE_COUNT_X;
  const pCountY = store.sceneParams.PLANE_COUNT_Y;
  const rowHeight = height / pCountY;
  const virtualHeight = height + rowHeight;
  const topBoundary = height / 2 + rowHeight / 2;

  for (let i = 0; i < planes.count; i++) {
    planes.getMatrixAt(i, tempMatrix);
    tempMatrix.decompose(currentPosition, currentQuaternion, currentScale);

    const row = Math.floor(i / pCountX);
    const initial_y_base = (row / pCountY) * height - height / 2;
    let target_y_base = initial_y_base + scrollOffset;
    target_y_base = topBoundary - ((topBoundary - target_y_base) % virtualHeight);

    const wave_offset = Math.sin(elapsedTime * store.sceneParams.IDLE_ANIM_FREQUENCY + i * 0.1) * store.sceneParams.IDLE_ANIM_AMPLITUDE;
    targetPosition.y = THREE.MathUtils.lerp(target_y_base, target_y_base + wave_offset, masterIntensity);
    targetPosition.x = currentPosition.x;

    const mountainFactor = (Math.cos(currentPosition.x / (width * 0.2)) + 1) / 2;
    const mouseDistance = Math.sqrt(Math.pow(currentPosition.x - smoothedMouse.x * (width/2), 2) + Math.pow(currentPosition.y - smoothedMouse.y * (height/2), 2));
    const rippleFactor = 1.0 - THREE.MathUtils.smoothstep(mouseDistance, 0, 15);
    targetPosition.z = mountainFactor * store.sceneParams.BULGE_AMOUNT + rippleFactor * store.sceneParams.MOUSE_RIPPLE_AMOUNT;

    let visibilityFactor = 1.0;
    const fadeZone = rowHeight * 1.5;
    if (target_y_base > height / 2 - fadeZone) {
      visibilityFactor = THREE.MathUtils.mapLinear(target_y_base, height / 2 - fadeZone, height / 2, 1.0, 0.0);
    } else if (target_y_base < -height / 2 + fadeZone) {
      visibilityFactor = THREE.MathUtils.mapLinear(target_y_base, -height / 2, -height / 2 + fadeZone, 0.0, 1.0);
    }
    visibilityFactor = THREE.MathUtils.clamp(visibilityFactor, 0.0, 1.0);
    visibilityAttribute.setX(i, visibilityFactor);

    if (isExiting) {
      targetScale.set(0.01, 0.01, 0.01);
    } else {
      const baseScaleValue = 1 + mountainFactor * store.sceneParams.BULGE_SCALE_AMOUNT + rippleFactor * store.sceneParams.MOUSE_RIPPLE_SCALE_AMOUNT;
      targetScale.set(baseScaleValue, baseScaleValue * (1 + mountainFactor * 0.5), baseScaleValue);
      targetScale.multiplyScalar(visibilityFactor);
      
      if (!isTransitioning && i === currentHoveredId) {
        targetScale.multiplyScalar(0.01);
      }
    }
    
    currentPosition.lerp(targetPosition, store.sceneParams.LERP_FACTOR);
    currentScale.lerp(targetScale, store.sceneParams.LERP_FACTOR);
    
    tempMatrix.compose(currentPosition, currentQuaternion, currentScale);
    planes.setMatrixAt(i, tempMatrix);
  }
  visibilityAttribute.needsUpdate = true;
  planes.instanceMatrix.needsUpdate = true;
}

// 【修改这里】
function updateInteraction() {
  if (isTransitioning || !invisibleHitbox || !raycaster || !invisibleHitbox.geometry) return;

  raycaster.setFromCamera(mouse, currentCamera);
  const intersects = raycaster.intersectObject(invisibleHitbox);

  if (intersects.length > 0) {
    const instanceId = intersects[0].instanceId;
    
    // 【新】直接从 hitbox 的 userData 中获取与 instanceId 对应的 point 数据
    const pointData = invisibleHitbox.userData[instanceId]?.point;

    // 只有当找到了有效数据，并且 instanceId 变化时，才更新状态
    if (pointData && instanceId !== currentHoveredId) {
        currentHoveredId = instanceId;
        store.setHoveredColor(pointData);
        hoveredPlaneTargetColor.set(pointData.color.hex);
        document.body.style.cursor = 'pointer';
    }
  } else if (currentHoveredId !== -1) {
    // 鼠标移出时重置
    currentHoveredId = -1;
    store.setHoveredColor(null);
    document.body.style.cursor = 'auto';
  }
}

function updateHoveredPlane() {
  if (hoveredPlane) {
    const isHovered = !isTransitioning && currentHoveredId !== -1;
    const lerpFactor = 0.08;
    hoveredPlaneCurrentColor.lerp(hoveredPlaneTargetColor, lerpFactor);

    if (isHovered && colorPlanes && colorPlanes.geometry) {
        hoveredPlane.visible = true;
        
        // 使用之前的 Bug Fix 逻辑，但参数从 store 读取
        const { width, height } = getCameraFrustum();
        const pCountX = store.sceneParams.PLANE_COUNT_X;
        const pCountY = store.sceneParams.PLANE_COUNT_Y;
        const row = Math.floor(currentHoveredId / pCountX);
        const col = currentHoveredId % pCountX;
        const xOffset = (row % 2) * (width / pCountX / 2);
        const baseX = (col / pCountX) * width - width / 2 + xOffset;
        const baseY = (row / pCountY) * height - height / 2;

        const targetPosition = new THREE.Vector3(
          baseX,
          baseY + 3.0,
          store.sceneParams.HOVER_Z_LIFT // 【改动】参数来自 store
        );
        const targetScale = new THREE.Vector3(
            store.sceneParams.HOVER_SCALE_FACTOR, // 【改动】参数来自 store
            store.sceneParams.HOVER_SCALE_FACTOR,
            store.sceneParams.HOVER_SCALE_FACTOR
        );

        hoveredPlane.position.lerp(targetPosition, lerpFactor);
        hoveredPlane.scale.lerp(targetScale, lerpFactor);
    } else {
        const targetScale = new THREE.Vector3(0, 0, 0);
        hoveredPlane.scale.lerp(targetScale, lerpFactor * 1.5);
        if (hoveredPlane.scale.x < 0.01) {
            hoveredPlane.visible = false;
        }
    }
  }
}

// --- 交互与辅助函数 ---
function onMouseMove(event) {
  const rect = event.currentTarget.getBoundingClientRect();
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
}
function onMouseLeave() { mouse.set(9999, 9999); }

// 【修改这里】
function onClick(event) {
  if (currentHoveredId !== -1) {
    // 【新】同样，直接从 hitbox 的 userData 中获取当前悬浮项的数据
    const pointData = invisibleHitbox.userData[currentHoveredId]?.point;
    
    if (pointData && !store.details.some(d => d.id === pointData.id)) {
        store.addDetail({ 
            id: pointData.id, 
            color: pointData.color, 
            element: pointData.element, 
            x: event.clientX, 
            y: event.clientY, 
            initial: true, 
            loadedHighRes: false 
        });
    }
  }
}

function handleResize() {
  if (currentCamera && renderer && sceneContainer.value) {
    const { clientWidth, clientHeight } = sceneContainer.value;
    const aspect = clientWidth / clientHeight;
    const frustumSize = 50;
    currentCamera.left = frustumSize * aspect / -2;
    currentCamera.right = frustumSize * aspect / 2;
    currentCamera.top = frustumSize / 2;
    currentCamera.bottom = frustumSize / -2;
    currentCamera.updateProjectionMatrix();
    renderer.setSize(clientWidth, clientHeight);
  }
}
function getCameraFrustum() {
  const frustumHeight = currentCamera.top - currentCamera.bottom;
  const frustumWidth = currentCamera.right - currentCamera.left;
  return { width: frustumWidth, height: frustumHeight };
}


</script>

<style scoped>
.scene-container {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  cursor: auto;
  pointer-events: none;
}
.scene-container > :deep(canvas) {
  pointer-events: auto;
}
</style>